import { type FC, type ReactNode } from "react";
import { useShopfrontDispatch } from "../../../../_Bootstrap/hooks";
import { showDialog } from "../../../../Actions/Dialogs/DialogActions";
import FontAwesomeLight from "../../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import FontAwesomeSolid from "../../../../Utilities/Platform/FontAwesome/FontAwesomeSolid";
import Button from "../../../Buttons/Button";
import Media from "../../../Media/Media";
import { MediaType } from "../../../Media/Selector/MediaUtilities";
import ColorPicker from "../../ColorPicker";
import BorderSettingsDialog, { type BorderSettingsType, createBorderStyles } from "../../DragDrop/BorderSettingsDialog";
import PaddingInput, { type PaddingType } from "../../PaddingInput";
import { type ComponentStyles } from "../DragDropTypes";
import { StyleInlineSelect } from "./StyleInlineSelect";
import { StyleSelect } from "./StyleSelect";

interface Props<T extends keyof ComponentStyles> {
    type: T;
    value: ComponentStyles[T];
    onChange: (value: ComponentStyles[T]) => void;
}

const MEDIA_FILTERS: Array<MediaType> = [ MediaType.Image ];

function getLabel(type: keyof ComponentStyles): string {
    switch(type) {
        case "color":
            return "Font Colour";
        case "background":
            return "Background Colour";
        case "borderColor":
            return "Border Colour";
        case "promotionColor":
            return "Promotion Colour";
        case "fontSize":
            return "Font Size";
        case "fontWeight":
            return "Font Weight";
        case "width":
            return "Width";
        case "height":
            return "Height";
        case "textAlign":
            return "Text Align";
        case "padding":
            return "Padding (in pixels)";
        case "image":
            return "Image";
        case "horizAlign":
            return "Horizontal Alignment";
        case "border":
            return "Border";

    }
}

export const DragDropStyleField: FC<Props<keyof ComponentStyles>> = ({
    type,
    value,
    onChange,
}) => {
    const dispatch = useShopfrontDispatch();

    const label = getLabel(type);

    let content: ReactNode;

    switch(type) {
        case "color":
        case "background":
        case "borderColor":
        case "promotionColor":
            content = (
                <ColorPicker
                    value={value as string}
                    onChange={onChange}
                />
            );
            break;
        case "fontSize":
            content = (
                <StyleSelect
                    options={[
                        { label: "8", value: "8" },
                        { label: "9", value: "9" },
                        { label: "10", value: "10" },
                        { label: "11", value: "11" },
                        { label: "12", value: "12" },
                        { label: "14", value: "14" },
                        { label: "16", value: "16" },
                        { label: "18", value: "18" },
                        { label: "24", value: "24" },
                        { label: "30", value: "30" },
                        { label: "36", value: "36" },
                        { label: "48", value: "48" },
                        { label: "60", value: "60" },
                        { label: "72", value: "72" },
                        { label: "96", value: "96" },
                    ]}
                    value={value as string}
                    onChange={onChange}
                />
            );
            break;
        case "fontWeight":
            content = (
                <StyleSelect
                    options={[
                        { label: "Normal", value: "normal" },
                        { label: "Bold", value: "bold" },
                        { label: "Bolder", value: "bolder" },
                    ]}
                    value={value as string}
                    onChange={onChange}
                />
            );
            break;
        case "width":
        case "height":
            content = (
                <input
                    type="text"
                    value={value as string}
                    onChange={e => {
                        onChange(e.currentTarget.value);
                    }}
                />
            );
            break;
        case "textAlign":
        case "horizAlign":
            content = (
                <StyleInlineSelect
                    options={[
                        { value: "left", label: <FontAwesomeSolid icon="align-left" /> },
                        { value: "center", label: <FontAwesomeSolid icon="align-center" /> },
                        { value: "right", label: <FontAwesomeSolid icon="align-right" /> },
                    ]}
                    value={value as "left" | "center" | "right"}
                    onChange={onChange}
                />
            );
            break;
        case "padding":
            content = (
                <PaddingInput
                    name={type}
                    padding={typeof value === "undefined" ? {
                        top   : 0,
                        left  : 0,
                        right : 0,
                        bottom: 0,
                    } : (value as PaddingType)}
                    onChange={(name, value) => {
                        onChange(value);
                    }}
                />
            );
            break;
        case "image":
            content = (
                <Media
                    type="image"
                    filters={MEDIA_FILTERS}
                    source={value as string}
                    onChange={onChange}
                    noSource={(
                        <div className="flex p-4 flex-col justify-center items-center">
                            <div className="text-4xl text-neutral-400 dark:text-neutral-500">
                                <FontAwesomeLight icon="image" />
                            </div>
                            <div className="text-neutral-600 dark:text-neutral-400">Select to Add Image</div>
                        </div>
                    )}
                />
            );
            break;
        case "border":
            // Renders the label differently
            return (
                <div className="border-settings">
                    {label}
                    <div className="border-settings-display-wrapper">
                        <div
                            className="border-settings-display"
                            style={createBorderStyles(value as BorderSettingsType)}
                        />
                    </div>
                    <Button
                        classes={[ "border-settings-edit-button", "button-neutral" ]}
                        onClick={(e): void => {
                            e.stopPropagation();

                            dispatch(showDialog(
                                "Border Settings",
                                <BorderSettingsDialog
                                    border={value as BorderSettingsType}
                                    onConfirm={onChange}
                                />,
                                "question"
                            ));
                        }}
                    >
                        <FontAwesomeLight icon="pencil" /> Edit Border
                    </Button>
                </div>
            );
    }

    return (
        <label className="block">
            {label}
            {content}
        </label>
    );
};
